import { create } from 'zustand';
import apiClient from './api/apiClient';

const integrationClientFirstSegment = (value) => {
    if (value == null || String(value).trim() === '') return '';
    return String(value).trim().split('-')[0].trim().toLowerCase();
};

const matchesIntegrationClientFilter = (clientFromReport, filterClient) => {
    if (!filterClient) return true;
    if (!clientFromReport) return false;
    return integrationClientFirstSegment(clientFromReport) === integrationClientFirstSegment(filterClient);
};

const useReportStore = create((set, get) => ({
    report: null,
    reports: [],
    totalReports: 0,
    totalPages: 0,
    retryLoading: {},
    activeFilters: {
        search: '',
        userId: '',
        orgId: '',
        toolId: '',
        filterFailed: null,
        integrationOnly: false,
        integrationClient: '',
        startDate: '',
        endDate: '',
    },

    setActiveFilters: (filters) => set({ activeFilters: { ...get().activeFilters, ...filters } }),

    setRetryLoading: (id, loadingState) => set((state) => ({
        retryLoading: { ...state.retryLoading, [id]: loadingState }
    })),

    setReport: (report) => set({ report }),

    updateReport: (report) => set((state) => {
        const existingReportIndex = state.reports.findIndex((r) => r.id === report.id);
        if (existingReportIndex !== -1) {
            return {
                reports: state.reports.map((r) => (r.id === report.id ? report : r)),
            };
        } else {
            return {
                reports: [report, ...state.reports],
            };
        }
    }),

    clearReport: () => set({ report: null }),

    fetchReports: async (page = 1, search = '', userId = '', orgId = '', toolId = '', filterFailed, integrationOnly = false, integrationClient = '', startDate = '', endDate = '') => {
        try {
            const params = {
                page,
                search,
                userId,
                orgId,
                toolId,
                filterFailed,
            };
            if (integrationOnly) {
                params.integrationOnly = true;
                if (integrationClient) {
                    params.integrationClient = integrationClient;
                }
            }
            if (startDate) {
                params.startDate = startDate;
            }
            if (endDate) {
                params.endDate = endDate;
            }
            const data = await apiClient.get('/admin/reports', params);
            
            set({
                reports: data.reports,
                totalReports: data.totalReports,
                totalPages: data.totalPages,
            });
        } catch (error) {
            console.error('Failed to fetch reports:', error);
        }
    },

    fetchReport: async (id) => {
        try {
            const data = await apiClient.get(`/admin/reports/${id}`);
            
            set((state) => {
                const existingReportIndex = state.reports.findIndex((report) => report.id === id);
                if (existingReportIndex !== -1) {
                    // Always update reports already in the list
                    return {
                        report: data.report,
                        reports: state.reports.map((report) =>
                            report.id === id ? data.report : report
                        ),
                    };
                } else {
                    // For new reports, check if they match the active filters
                    const { activeFilters } = state;
                    const report = data.report;

                    if (activeFilters.search?.trim()) {
                        return { report: data.report };
                    }
                    if (activeFilters.userId && report.user_id !== activeFilters.userId) {
                        return { report: data.report };
                    }
                    if (activeFilters.orgId && report.org_id !== activeFilters.orgId) {
                        return { report: data.report };
                    }
                    if (activeFilters.toolId && report.tool_id !== activeFilters.toolId) {
                        return { report: data.report };
                    }
                    if (activeFilters.filterFailed && !(report.status === 'completed' && !report.report_fetched)) {
                        return { report: data.report };
                    }
                    if (activeFilters.integrationOnly && !report.partner) {
                        return { report: data.report };
                    }
                    if (activeFilters.integrationClient && !matchesIntegrationClientFilter(report.partner, activeFilters.integrationClient)) {
                        return { report: data.report };
                    }
                    const reportDay = report.created_at
                        ? new Date(report.created_at).toISOString().slice(0, 10)
                        : null;
                    if (activeFilters.startDate && reportDay && reportDay < activeFilters.startDate) {
                        return { report: data.report };
                    }
                    if (activeFilters.endDate && reportDay && reportDay > activeFilters.endDate) {
                        return { report: data.report };
                    }

                    return {
                        report: data.report,
                        reports: [data.report, ...state.reports].sort((a, b) => b.row_number - a.row_number),
                    };
                }
            });
        } catch (error) {
            console.error('Failed to fetch report:', error);
        }
    },

    deleteReport: async (id) => {
        try {
            await apiClient.delete(`/admin/reports/${id}`);
            set((state) => ({
                reports: state.reports.filter((report) => report.id !== id),
            }));
        } catch (error) {
            console.error('Failed to delete report:', error);
        }
    },

    editReport: async (id, updatedData) => {
        try {
            const data = await apiClient.patch(`/admin/reports/${id}`, updatedData);
            
            set((state) => ({
                reports: state.reports.map((report) =>
                    report.id === id ? data.report : report
                ),
                report: data.report,
            }));
            
            console.log("Report updated: ", data.report);
        } catch (error) {
            console.error('Failed to edit report:', error);
        }
    },
}));

export default useReportStore;
